// this namespace is additionally compiled standalone into ../korum-platform/resources/validate.js
// to run server-side validation

const isEmpty = (value: any) => value !== false && (!value || (value.trim && value.trim() === '') || (value.length === 0));

const emptyError = (state: object, field: string, errors: object, prefix?: string, suffix?: string) => {
    const value = (state || {})[field];
    if (isEmpty(value)) {
        errors[(prefix || '') + field + (suffix || '')] = true;
    }
    return errors;
};

export const passwordMessage = 'Password requires at least 5 characters';

const passwordError = (state: object, field: string, errors: object) => {
    const value = state[field];
    if (!value || value.trim().length < 5) {
        errors[field] = true;
    }
};

export const emptyErrors = (state: object, fields: string[], errors: object, prefix?: string, suffix?: string) => {
    for (const field of fields) {
        emptyError(state, field, errors, prefix, suffix);
    }
    return errors;
};

export const validateLogin = (email: string, password: string): object => {
    const result = {};
    if (!email) {
        result['email'] = true;
    }
    if (!password) {
        result['password'] = true;
    }
    return result;
};

export const validateReset = (email: string): object => {
    const result = {};
    if (!email) {
        result['email'] = true;
    }
    return result;
};

export const validateContract = (state: object): object => {
    const errors = {};
    emptyErrors(state, ['newDoc', 'newPdf', 'category'], errors);
    return errors;
};

export const validateClientRegister = (state: object): object => {
    const errors = {};
    emptyErrors(state, ['firstName', 'lastName'], errors);
    return errors;
};

export const validateClientRegister2 = (state: object): object => {
    const errors = {};
    emptyErrors(state, ['companyName', 'companyNumber', 'companyAddress', 'registrationCertificate', 'directorName', 'directorID', 'directorAddress', 'clientContactNumber'], errors);
    return errors;
};

export const validateConsultantRegister = (state: any): object => {
    const errors = {};
    let fields = [
        'email',
        'firstName',
        'lastName',
        'address',
        'residenceCity',
        'country',
        'phoneNumber',
        'howDidYouHear',
        'visaAuthorization',
        'beenStruckOff',
        'languages',
        'currentSalary',
        'salaryUnits'
    ];
    if (state.country === 'Other') {
        fields.push('countryOther');
    }
    if (!state.review) {
        fields = fields.concat('passportID', 'currentCV');
    }
    if (!state.availableFullTime && !state.availablePartTime) {
        errors['availability'] = true;
    }
    emptyErrors(state, fields, errors);
    if (state.engagementType === 'Professional services company') {
        const extraFields = [
            'consultantCompanyName',
            'companyRegistrationNumber',
            'address',
            'city',
            'country'
        ];
        emptyErrors(state.professionalServicesInfo, extraFields, errors, 'professionalServicesInfo.');
        emptyError(state, 'professionalServicesRegistration', errors);
    }
    if (state.visaAuthorization) {
        const extraFields = [
            'visaExpiration',
            'visaProof'
        ];
        emptyErrors(state, extraFields, errors);
    }
    return errors;
};

export const validateReset2 = (state: object): object => {
    const errors = {};
    passwordError(state, 'password1', errors);
    if (state['password1'] !== state['password2']) {
        errors['password2'] = true;
    }
    return errors;
};

export const validateReset3 = (state: object): object => {
    const errors = validateReset2(state);
    emptyError(state, 'originalPassword', errors);
    return errors;
};

export const validateClientCreate = (state: object): object => {
    const errors = {};
    passwordError(state, 'password1', errors);
    emptyErrors(state, ['firstName', 'lastName', 'companyName', 'jobTitle', 'timezone'], errors);
    if (state['password1'] !== state['password2']) {
        errors['password2'] = true;
    }
    return errors;
};

export const validateConsultantCreate = (state: object): object => {
    const errors = {};
    passwordError(state, 'password1', errors);
    emptyErrors(state, ['timezone', 'defaultAvailability'], errors);
    if (state['password1'] !== state['password2']) {
        errors['password2'] = true;
    }
    return errors;
};

export const validateSelfProfile = (state: object): object => {
    const errors = {};
    emptyErrors(state, ['timezone'], errors);
    return errors;
};

export const validateInvite = (state: any): object => {
    const errors = {};
    emptyErrors(state, ['email', 'accountType', 'firstName', 'lastName'], errors);
    if (state.email.includes('<') || state.email.includes('>')) {
        errors['email'] = true;
    }
    return errors;
};

export const validateKYC = (state: any, kycIndex: number): object => {
    const errors = {};
    const check = (nums: number[], ks: string[]) => {
        if (nums.some(n => n === kycIndex)) {
            emptyErrors(state, ks, errors);
        }
    };
    check([0, 1, 2], ['companyName', 'companyNumber', 'companyAddress', 'companyAddress2']);
    check([0], ['annualReport']);
    check([1, 2], ['incorporationCertificate', 'companyDirector']);
    check([3, 4], ['fullName', 'residentialAddress', 'residentialAddress2']);
    check([1, 2], ['directorID']);
    check([3, 4], ['personalID', 'proofOfAddress']);
    if (kycIndex === 4) {
        const toCheck = ['registrationCertificate', 'companyNumber'];
        if (!state.businessNameFullName) {
            toCheck.push('businessName');
        }
        if (!state.businessAddressResidential) {
            toCheck.push('businessAddress', 'businessAddress2');
        }
        emptyErrors(state, toCheck, errors);
    }
    check([2], ['ownershipStructure', 'countryDetails']);
    return errors;
};

export const validateProposalMsg = (state: object, i: number) => {
    const errors = {};
    emptyErrors(state, ['name', 'email', 'msg'], errors, '', `${i}`);
    return errors;
};

export const validateProposal = (state: any) => {
    const errors = {};
    const toValidate = ['proposalTitle'];
    if (!state.startDateTBC) {
        toValidate.push('startDate');
    }
    if (!state.endDateTBC) {
        toValidate.push('endDate');
    }
    emptyErrors(state, toValidate, errors);
    return errors;
};

export const validateLoginServer = (email: string, password: string): boolean => {
    return Object.keys(validateLogin(email, password)).length === 0;
};

export const validateResetServer = (email: string): boolean => {
    return Object.keys(validateReset(email)).length === 0;
};

export const validateClientRegisterServer = (firstName: string, lastName: string, jobTitle: string, companyName: string, email: string, phone: string, query: string, tnc: boolean): boolean => {
    return Object.keys(validateClientRegister({firstName, lastName, jobTitle, companyName, email, phone, query, tnc})).length === 0;
};

export const validateClientRegister2Server = (stateString: string): boolean => {
    return Object.keys(validateClientRegister2(JSON.parse(stateString))).length === 0;
};

export const validateConsultantRegisterServer = (stateString: string): boolean => {
    const state = JSON.parse(stateString);
    return Object.keys(validateConsultantRegister(state)).length === 0;
};

export const validateReset2Server = (password1: string, password2: string): boolean => {
    return Object.keys(validateReset2({password1, password2})).length === 0;
};

export const validateReset3Server = (originalPassword: string, password1: string, password2: string): boolean => {
    return Object.keys(validateReset3({originalPassword, password1, password2})).length === 0;
};

export const validateClientCreateServer = (stateString: string): boolean => {
    return Object.keys(validateClientCreate(JSON.parse(stateString))).length === 0;
};

export const validateConsultantCreateServer = (stateString: string): boolean => {
    return Object.keys(validateConsultantCreate(JSON.parse(stateString))).length === 0;
};



// WEBPACK FOOTER //
// ./src/validate.tsx